import * as React from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import * as Yup from 'yup';
import DatePicker from 'react-datepicker';
import i18next from 'i18next';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { connect } from 'react-redux';
import moment from 'moment';
import { Field as FieldContainer, FieldBody, Control, Container, Button } from 'bloomer';
import { Formik, Form, Field, FormikHelpers, FormikErrors } from 'formik';
import Card from '../Card/Card';
import SelectField from '../SelectField/SelectField';
import FormError from '../FormError/FormError';
import { FormField } from '../FormField/FormField';
import { GetBarcodeFormPayload, Barcode, BarcodeKitStatus } from '../../models/barcode';
import { AppState } from '../../store';
import { actionGetBarcodeDetails } from '../../store/barcode/action';
import { actionSaveBasicHealth } from '../../store/health/action';
import { actionActivateKit } from '../../store/kit/action';
import { KitActivateFormPayload } from '../../models/kit';
import { Gender, GENDER, Ethnicity, getEthnicityByPartner, Partner, PhoneType, CountryCode, EmailTagName, getPartnerPhoneNumber } from '../../helpers/types';
import { FormTitle, FormText, FormWidth, ConsentContainer, ButtonContainer, PopUpTitle, ContryCodeContainer } from '../../styles/styled-components/Form/FormComponent';
import { getPartner, getCustomerID, getRootUSer } from '../../constants';
import { PayloadUserProfile, CreateProfileResult, ProfileName, ProfileHealth, ProfilePreference, ProfileEmail, Profile, Tag, TransferEmail, ProfileTransfer } from '../../models/profile';
import { stripHypen, isValidMobilePhone } from '../../helpers/helpers';
import HealthService from '../../store/health/effects';
import { CreatePopUpSubHeading } from '../../styles/styled-components/User/User';
import styled from 'styled-components';
import { actionDestinationAccountExist } from '../../store/profileTransfer/actions';
import SelectFieldCountryCode from '../SelectField/SelectFieldCountryCode';
import { getCurrentAppLang } from '../../helpers/util';

interface CreateProfileStates {
  loader: boolean;
  barcode: string;
  firstName: string;
  lastName: string;
  dob: string;
  email: string;
  sex: string | Gender;
  countryCode: string;
  ethnicity: string;
  phone: string;
  consent?: boolean;
  privacy?: boolean;
  isValidBarcode?: boolean;
  partner: string;
  owner: string;
  userProfile: Profile;
  isCreate: boolean;
}
type CreateProfileProps = {
  validateBarcode?: (payload: GetBarcodeFormPayload) => void;
  saveUserProfile?: (payload: PayloadUserProfile) => void;
  activateKit?: (payload: KitActivateFormPayload) => void;
  checkDestinationAccountExist?: (payload: TransferEmail) => void;
  barcode?: Barcode;
  profile?: CreateProfileResult;
  transfer?: ProfileTransfer;
  hidePopup: any;
};
class CreateProfile extends React.Component<CreateProfileProps, CreateProfileStates> {

  state: CreateProfileStates = {
    loader: false,
    barcode: '',
    firstName: '',
    lastName: '',
    dob: '',
    email: '',
    phone: '',
    sex: '',
    countryCode: getPartner() === Partner.ARTMED ? CountryCode.tw : CountryCode.vi,
    ethnicity: '',
    consent: false,
    privacy: false,
    isValidBarcode: false,
    partner: '',
    owner: '',
    userProfile: { profile: {} },
    isCreate: false,
  };
  async handleSubmit(values: CreateProfileStates, actions: FormikHelpers<CreateProfileStates>) {
    if (this.state.isValidBarcode) {
      if (values.consent && values.privacy) {
        actions.setStatus({ error: '' });
        this.setState({ loader: true });
        this.setState({
          dob: values.dob,
          firstName: values.firstName,
          lastName: values.lastName,
          barcode: stripHypen(values.barcode),
          email: values.email,
          sex: values.sex,
          ethnicity: values.ethnicity,
          countryCode: values.countryCode,
          phone: values.phone,
        });

        this.props.checkDestinationAccountExist && await this.props.checkDestinationAccountExist({ email: values.email });
        if (this.props.transfer && this.props.transfer.isExistAccount) {
          this.setState({ isCreate: true });
        } else {
          await this.createProfile();
        }
        this.setState({ loader: false });
      } else {
        actions.setStatus({ error: 'form.generic.error' });
      }
    }
  }
  handleFormatBarcode(e: React.ChangeEvent<HTMLInputElement>, setFieldValue: any) {
    setFieldValue('barcode', this.formatBarcode(e.target.value));
  }
  async validateBarcodeHandler(e: React.ChangeEvent<HTMLInputElement>, setFieldTouched: any, setErrors: any, prevErrors: FormikErrors<CreateProfileStates>) {
    setFieldTouched('barcode', true);
    await this.validateBarcodes(e.target.value, setErrors, prevErrors);
  }

  createProfile = async () => {
    this.setState({ loader: true });
    const { dob,
      firstName,
      lastName,
      barcode,
      email,
      sex,
      ethnicity,
      countryCode,
      phone } = this.state;

    const dobFormat = moment(dob).format('YYYY-MM-DD');
    const tagContent = `${EmailTagName.tag}:${email}`;
    const profileName: ProfileName = {
      lastName,
      firstName,
    };
    const profileHealth: ProfileHealth = {
      dob: dobFormat,
      gender: sex,
      ethnicity,
    };
    const profileEmail: ProfileEmail = {
      name: EmailTagName.b2bpartner,  //  required to identify the partner
      primary: true,
      detail: {
        email: getRootUSer(),
      },
    };
    const language = getCurrentAppLang(true).split('_')[1];
    const profilePreference: ProfilePreference = {
      language,
    };
    const partner = getPartner();
    const owner = getCustomerID();
    const tag: Tag = {
      tag: tagContent,
    };
    const partnerTag: Tag = {
      tag: partner + '-portal',
    };
    const userProfile: Profile = {
      profile: {
        name: profileName,
        email: profileEmail,
        health: profileHealth,
        preference: profilePreference,
        phone: {
          name: PhoneType.MOBILE,
          primary: true,
          detail: {
            countryCode,
            number: phone,
          },
        },
        tag,
      },
    };
    const newUserProfile: PayloadUserProfile = {
      profile: userProfile,
      partner,
      owner,
    };
    this.props.saveUserProfile && await this.props.saveUserProfile(newUserProfile);
    const { profile, activateKit } = this.props;
    const editProfile: Profile = {
      profile: {
        email: {
          name: EmailTagName.b2bcustomer, // required to identify the patient
          primary: false,
          detail: {
            email,
          },
        },
      },
    };
    await HealthService.updateEmail({
      isName: false,
      isHeath: false,
      isPhone: false,
      profileId: profile?.profileId || '',
      profile: editProfile,
      partner: getPartner(),
      owner: getCustomerID(),
    });
    await HealthService.updateTag({
      isName: false,
      isHeath: false,
      isPhone: false,
      profileId: profile?.profileId || '',
      profile: {
        profile: {
          tag: partnerTag,
        },
      },
      partner: getPartner(),
      owner: getCustomerID(),
    });
    if (profile && activateKit) {
      await activateKit({ code: barcode, profileId: profile.profileId, ...newUserProfile });
      this.setState({ loader: false });
      this.props.hidePopup && this.props.hidePopup(true);
    }
    this.setState({ loader: false });
  };
  formatBarcode(value: string) {
    const regex = new RegExp('.{1,4}', 'g');
    let val = stripHypen(value);
    if (val.length) {
      val = val.match(regex)!.join('-');
    }
    return val;
  }
  async validateBarcodes(value: string, setErrors: any, prevErrors: FormikErrors<CreateProfileStates>) {
    if (value && value.length >= 17) {
      const { validateBarcode } = this.props;
      if (validateBarcode) {
        await validateBarcode({ barcode: stripHypen(value) });
        const { barcode } = this.props;
        if (barcode) {
          if (barcode.error || (this.props.barcode && [BarcodeKitStatus.ORDERED, BarcodeKitStatus.LAB].includes(barcode.status) === false)) {
            setErrors({ ...prevErrors, barcode: 'form.barcode.error.invalid' });
          } else {
            this.setState({ isValidBarcode: true });
          }
        } else {
          this.setState({ isValidBarcode: true });
        }
      }
    } else {
      setErrors({ ...prevErrors, barcode: 'form.barcode.error.incorrect14' });
    }
  }

  render() {
    const { isCreate, loader } = this.state;
    const PARTNER = getPartner();
    const ETHNICITY = getEthnicityByPartner(PARTNER as Partner);
    const COUNTRY = getPartnerPhoneNumber(PARTNER as Partner);
    const firstNameLength: number = (PARTNER === 'ogath' || PARTNER === 'bdmsth') ? 30 : 12;
    const lastNameLength: number = (PARTNER === 'ogath' || PARTNER === 'bdmsth') ? 30 : 13;
    const validationSchema = Yup.object().shape({
      barcode: Yup.string().min(17, 'form.barcode.error.incorrect14').max(17, 'form.barcode.error.invalid').required('form.barcode.error.empty'),
      email: Yup.string().required().email('form.email.error.invalidFormat').required('form.email.error.invalidFormat'),
      firstName: Yup.string().required('form.firstName.error.empty'),
      lastName: Yup.string().required('form.lastName.error.empty'),
      dob: Yup.date().required('form.dob.error.empty'),
      sex: Yup.string().required('form.sex.error.empty'),
      countryCode: Yup.string().required('country code'),
      ethnicity: Yup.string().required('form.ethnicity.error.empty'),
      phone: Yup.string()
        .required('form.phone.error.invalidFormat')
        .test('phone', 'form.phone.error.invalidFormat', isValidMobilePhone),
      consent: Yup.bool().notRequired().oneOf([true, false], 'profile.consent.error.1'),
      privacy: Yup.bool().notRequired().oneOf([true, false], 'profile.privacy.error.1'),
    });
    return (
      <div className="CreateProfile">
        <div className="form">
          <Card>
            <Formik initialValues={this.state}
              onSubmit={(values, actions) => this.handleSubmit(values, actions)}
              validateOnChange={true}
              validationSchema={validationSchema}
              render={({ values, errors, touched, status, setFieldValue, setFieldTouched, setErrors }) => (
                <Form method="POST">
                  {!isCreate ?
                    <Container>
                      <FormTitle>{i18next.t('cnc.header')}</FormTitle>
                      <FormText />
                      <FormError data={status} />
                      <FormField name="barcode" label={i18next.t('cmp.table.title.barcode')} >
                        <Field name="barcode" className="input" maxLength={200} placeholder={i18next.t('form.barcode.placeholder')} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleFormatBarcode(e, setFieldValue)} onBlur={(e: React.ChangeEvent<HTMLInputElement>) => this.validateBarcodeHandler(e, setFieldTouched, setErrors, errors)} />
                      </FormField>
                      <FormField name="email" label={i18next.t(`cmp.table.title.email`)} >
                        <Field name="email" className="input" maxLength={200} placeholder={i18next.t('form.email.placeholder')} />
                      </FormField>
                      <FormField name="firstName" label={i18next.t(`cmp.table.title.firstName`)} >
                        <Field name="firstName" maxLength={firstNameLength} className="input" placeholder={i18next.t('form.firstName.placeholder')} />
                      </FormField>
                      <FormField name="lastName" label={i18next.t(`cmp.table.title.lastName`)} >
                        <Field name="lastName" maxLength={lastNameLength} className="input" placeholder={i18next.t('form.lastName.placeholder')} />
                      </FormField>
                      <FieldContainer isHorizontal>
                        <FieldBody>
                          <FormWidth>
                            <FieldContainer className="half">
                              <Control>
                                <FormField name="sex" label={i18next.t(`cmp.table.title.gender`)} >
                                  <SelectField name="sex" className="input">
                                    <option value="">{i18next.t('form.sex.placeholder')}</option>
                                    {
                                      Object.keys(GENDER).map((value, key) => <option value={value} key={key}>{GENDER[value as Gender]}</option>)
                                    }
                                  </SelectField>
                                </FormField>
                              </Control>
                            </FieldContainer>
                          </FormWidth>
                          <FormWidth>
                            <FieldContainer className="half">
                              <Control>
                                <FormField name="ethnicity" label={i18next.t(`cmp.table.title.ethnicity`)} >
                                  <SelectField name="ethnicity" className="input">
                                    <option value="">{i18next.t('form.ethnicity.placeholder')}</option>
                                    {
                                      Object.keys(ETHNICITY).map((value, key) => <option value={value} key={key}>{ETHNICITY[value as Ethnicity]}</option>)
                                    }
                                  </SelectField>
                                </FormField>
                              </Control>
                            </FieldContainer>
                          </FormWidth>
                        </FieldBody>
                      </FieldContainer>
                      <FieldContainer>
                        <Control>
                          <FormField name="dob" label={i18next.t('form.dob.label')} >
                            <DatePickerField
                              name="dob"
                              value={values.dob}
                              onChange={setFieldValue}
                            />
                            <span className="icon" />

                          </FormField>
                        </Control>
                      </FieldContainer>

                      <FieldContainer isHorizontal>
                        <FieldBody>
                          <FieldContainer className="half">
                            <ContryCodeContainer>
                              <Control>
                                <FormField name="phone" label={i18next.t(`cmp.table.title.phone`)} >
                                  <div className="flex">
                                    <SelectFieldCountryCode name="countryCode"
                                      className="input">
                                      <option value="" />
                                      {
                                        Object.keys(COUNTRY).map((value, key) => <option value={value} key={key}>{COUNTRY[value as CountryCode]}</option>)
                                      }
                                    </SelectFieldCountryCode>
                                    <Field name="phone" maxLength={11} className="input" placeholder={i18next.t('form.phone.placeholder')} />
                                  </div>
                                </FormField>
                              </Control>
                            </ContryCodeContainer>
                          </FieldContainer>
                          <FieldContainer className="half">
                            <Control>
                              <FormField name="uniqid" label={i18next.t('form.uniqueID.label')} >
                                <Field name="uniqid" maxLength={20} className="input" placeholder={i18next.t('form.uniqueID.label')} />
                              </FormField>
                            </Control>
                          </FieldContainer>
                        </FieldBody>
                      </FieldContainer>

                      <ConsentContainer>
                        <span>
                          <Field
                            name="consent"
                            render={({ field }: any) => {
                              return (
                                <input type="checkbox" checked={field.value} {...field} />
                              );
                            }}
                          />
                        </span>
                        <span>
                          <span className="link" onClick={() => { }}>{i18next.t('cnc.consent.text.0')}</span>
                          <span className="link" onClick={() => { }} />
                          <FormError>{touched.consent && errors.consent ? errors.consent : ''}</FormError>
                        </span>
                      </ConsentContainer>

                      <ConsentContainer>
                        <span>
                          <Field
                            name="privacy"
                            render={({ field }: any) => {
                              return (
                                <input type="checkbox" checked={field.value} {...field} />
                              );
                            }}
                          />
                        </span>
                        <span>
                          {i18next.t('cnc.consent.text.1')}
                          <FormError>{touched.privacy && errors.privacy ? errors.consent : ''}</FormError>
                        </span>
                      </ConsentContainer>
                      <ButtonContainer>
                        <div><button className="button" onClick={() => this.closePopUp()} disabled={loader}>{i18next.t('cnc.cancelButton')}</button></div>
                        {/* <div><button className="button" type="submit">{i18next.t('cnc.createButton')}</button></div> */}
                        <Button isLoading={loader} type="submit">{i18next.t('cnc.createButton')}</Button>
                      </ButtonContainer>
                    </Container>
                    : <ActivationContainer>
                      <Container>
                        <PopUpTitle>{i18next.t('cnc.accountExists.popUp.title')}</PopUpTitle>
                        <FormText />
                        <CreatePopUpSubHeading>{i18next.t('cnc.accountExists.popUp.text')}</CreatePopUpSubHeading>
                        <ButtonContainer>
                          <div><button className="button" onClick={() => this.hidePopUp()} disabled={loader}>{i18next.t('cnc.accountExists.popUp.cancelButton')}</button></div>
                          {/* <div><button className="button" type="submit" onClick={() => this.createProfile()}>{i18next.t('cnc.accountExists.popUp.confirmButton')}</button></div> */}
                          <Button isLoading={loader} onClick={() => this.createProfile()}>{i18next.t('cnc.accountExists.popUp.confirmButton')}</Button>
                        </ButtonContainer>
                      </Container>
                    </ActivationContainer>
                  }
                </Form>
              )}
            />
          </Card>
        </div>
      </div>
    );
  }
  closePopUp = () => {
    this.props.hidePopup && this.props.hidePopup();
  };
  hidePopUp = () => {
    this.setState({
      isCreate: false,
    });
  };
}

const DatePickerField = ({ name, value, onChange }: any) => {
  const dob = value ? new Date(value) : null;
  return (
    <DatePicker
      selected={dob}
      className={'input'}
      placeholderText={i18next.t('form.dob.placeholder')}
      showYearDropdown
      strictParsing
      dateFormat="dd-MMM-yyyy"
      scrollableYearDropdown
      yearDropdownItemNumber={150}
      maxDate={new Date()}
      onChange={val => {
        onChange(name, val);
      }}
    />
  );
};

export const ActivationContainer = styled.div`
	width:100%;
	margin-top:-1.5em;
	margin-bottom:-4em;
`;
const mapStateToProps = (state: AppState, ownProps: CreateProfileProps) => {
  return {
    profile: state.profile,
    barcode: state.barcode,
    transfer: state.profileTransfer,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    validateBarcode: (payload: GetBarcodeFormPayload) => dispatch(actionGetBarcodeDetails(payload)),
    saveUserProfile: (payload: PayloadUserProfile) => dispatch(actionSaveBasicHealth(payload)),
    activateKit: (payload: KitActivateFormPayload) => dispatch(actionActivateKit(payload)),
    checkDestinationAccountExist: (payload: TransferEmail) => dispatch(actionDestinationAccountExist(payload)),

  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateProfile);